/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { ButtonBase } from "../ButtonBase";

import "./Tab.scss";

export interface ITabProps {
	className?: string;
	label?: string;
	value?: string;
	selected?: boolean;
	disabled?: boolean;
	onChange?: (event: React.MouseEvent, value?: string) => void;
	indicator?: React.ReactNode;
}

export const Tab: React.FC<ITabProps> = ({
	className,
	label,
	value,
	selected,
	disabled,
	onChange,
	indicator
}) => {
	const baseClass = BEM("edt-tab", null, {
		disabled
	});

	const handleClick = useCallback<React.MouseEventHandler>(
		(event) => {
			if (isFunction(onChange)) {
				onChange(event, value);
			}
		},
		[ onChange, value ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<ButtonBase
			className={baseClass.toClassName()}
			disabled={disabled}
			onClick={handleClick}
			role="tab"
			aria-selected={selected}
			tabIndex={selected ? 0 : -1}
		>
			{label}
			{indicator}
		</ButtonBase>
	);
};
